import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, FileText, ShoppingBag, Loader2, Check } from "lucide-react";
import client, { displayName, priceLabel, hasPrice } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const Checkout = () => {
  const { cart, clearCart } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("order");
  const [notes, setNotes] = useState("");
  const [addr, setAddr] = useState({ line1: "", city: "", postal_code: "", country: "Canada" });
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (user) { setName(user.name || ""); setCompany(user.company || ""); }
    if (user?.address) setAddr({ line1: user.address.line1 || "", city: user.address.city || "", postal_code: user.address.postal_code || "", country: user.address.country || "Canada" });
  }, [user]);

  useEffect(() => {
    if (!cart.length) { setItems([]); return; }
    client.post("/products/by-skus", { skus: cart.map((i) => i.sku) }).then((r) => setItems(r.data.items));
  }, [cart.map((i) => i.sku).join(",")]);

  const qtyOf = (sku) => cart.find((i) => i.sku === sku)?.qty || 0;
  const priced = items.filter((p) => hasPrice(p));
  const total = items.reduce((sum, p) => sum + (hasPrice(p) ? Number(p.admin_price) * qtyOf(p.sku) : 0), 0);
  const allPriced = items.length > 0 && priced.length === items.length;
  const setA = (k) => (e) => setAddr((s) => ({ ...s, [k]: e.target.value }));

  const place = async () => {
    if (type === "quote" && (!name.trim() || !company.trim())) {
      toast.error("Name and company are required for a quote request");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await client.post("/orders", {
        type, notes, address: addr, name, company,
        items: cart.map((i) => ({ sku: i.sku, qty: i.qty })),
      });
      clearCart();
      toast.success(type === "quote" ? "Quote request sent!" : "Order placed!", { description: `Reference ${data.order_no}` });
      navigate(`/account/orders/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to place order"); }
    finally { setPlacing(false); }
  };

  if (!cart.length) {
    return (
      <div className="min-h-screen flex flex-col bg-white"><Navbar />
        <div className="flex-1 grid place-items-center"><div className="text-center"><ShoppingBag className="w-12 h-12 text-steel-light mx-auto mb-3" /><p className="text-steel">Your cart is empty.</p><Button className="mt-3 bg-orange hover:bg-orange-hover" onClick={() => navigate("/catalogue")}>Browse Catalogue</Button></div></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white"><Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 w-full py-8 flex-1">
        <h1 className="text-2xl font-bold tracking-tight text-graphite mb-6">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* type */}
            <div className="border border-border rounded-lg p-5">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Order Type</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button data-testid="type-order" onClick={() => setType("order")} className={`text-left p-4 rounded-md border-2 transition-colors ${type === "order" ? "border-orange bg-orange/5" : "border-border"}`}>
                  <div className="flex items-center gap-2 font-semibold text-graphite"><ShoppingBag className="w-4 h-4" /> Place Order {type === "order" && <Check className="w-4 h-4 text-orange ml-auto" />}</div>
                  <p className="text-xs text-steel-light mt-1">Submit a purchase order. Our team confirms availability & pricing.</p>
                </button>
                <button data-testid="type-quote" onClick={() => setType("quote")} className={`text-left p-4 rounded-md border-2 transition-colors ${type === "quote" ? "border-orange bg-orange/5" : "border-border"}`}>
                  <div className="flex items-center gap-2 font-semibold text-graphite"><FileText className="w-4 h-4" /> Request Quote {type === "quote" && <Check className="w-4 h-4 text-orange ml-auto" />}</div>
                  <p className="text-xs text-steel-light mt-1">Send your cart as a quotation request for items without listed prices.</p>
                </button>
              </div>
            </div>

            {/* contact (required for quote) */}
            <div className="border border-border rounded-lg p-5">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Contact {type === "quote" && <span className="text-orange">· required for quotes</span>}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label htmlFor="co-name">Name {type === "quote" && "*"}</Label><Input id="co-name" data-testid="checkout-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" required={type === "quote"} /></div>
                <div><Label htmlFor="co-company">Company {type === "quote" && "*"}</Label><Input id="co-company" data-testid="checkout-company" value={company} onChange={(e) => setCompany(e.target.value)} className="mt-1.5 h-11" required={type === "quote"} /></div>
              </div>
            </div>

            {/* address */}
            <div className="border border-border rounded-lg p-5">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Delivery Address</div>
              <div className="space-y-3">
                <div><Label htmlFor="line1">Address</Label><Input id="line1" data-testid="checkout-line1" value={addr.line1} onChange={setA("line1")} className="mt-1.5 h-11" /></div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label htmlFor="city">City</Label><Input id="city" data-testid="checkout-city" value={addr.city} onChange={setA("city")} className="mt-1.5 h-11" /></div>
                  <div><Label htmlFor="pc">Postal code</Label><Input id="pc" data-testid="checkout-postal" value={addr.postal_code} onChange={setA("postal_code")} className="mt-1.5 h-11" /></div>
                  <div><Label htmlFor="country">Country</Label><Input id="country" value={addr.country} onChange={setA("country")} className="mt-1.5 h-11" /></div>
                </div>
              </div>
            </div>

            {/* notes */}
            <div className="border border-border rounded-lg p-5">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Notes (optional)</div>
              <Textarea data-testid="checkout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="PO number, urgency, special instructions…" rows={3} />
            </div>
          </div>

          {/* summary */}
          <div className="lg:col-span-1">
            <div className="border border-border rounded-lg p-5 sticky top-24">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Summary</div>
              <div className="space-y-3 max-h-64 overflow-y-auto thin-scroll mb-3">
                {items.map((p) => (
                  <div key={p.sku} className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded gp-card-img grid place-items-center p-1 shrink-0">{p.main_image ? <img src={p.main_image} alt="" className="max-h-full object-contain mix-blend-multiply" /> : <Box className="w-4 h-4 text-steel-light" />}</div>
                    <div className="min-w-0 flex-1"><div className="mono text-[11px] text-orange truncate">{p.sku}</div><div className="text-xs text-graphite truncate">{displayName(p)}</div></div>
                    <div className="text-right"><div className="mono text-xs">×{qtyOf(p.sku)}</div><div className="text-xs text-steel-light">{hasPrice(p) ? `$${(Number(p.admin_price) * qtyOf(p.sku)).toFixed(2)}` : "—"}</div></div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-3 flex justify-between text-sm">
                <span className="text-steel-light">{allPriced ? "Estimated total" : "Total"}</span>
                <span className="font-bold text-graphite">{allPriced ? `$${total.toFixed(2)} CAD` : (type === "quote" ? "Quote pending" : "Pricing on confirmation")}</span>
              </div>
              <Button data-testid="place-order" onClick={place} disabled={placing} className="w-full mt-4 h-11 bg-orange hover:bg-orange-hover">
                {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : (type === "quote" ? "Send Quote Request" : "Place Order")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;
